import React, { useEffect } from 'react';
import clsx from 'clsx';
import { Button, Icon } from '@/components/ui';
import { useCreateAccountForm } from './useCreateAccountForm';
import { LabeledField } from './LabeledField';
import { ColorSwatchGrid } from './ColorSwatchGrid';
import { LeadSelect } from './LeadSelect';
import { AccountsTab } from '../lib/deriveRows';

interface CreateAccountSheetProps {
  open: boolean;
  tab: AccountsTab;
  onClose: () => void;
}

const TYPE_OPTIONS = [
  { value: 2, label: 'Internal' },
  { value: 3, label: 'Private' },
];

export const CreateAccountSheet = ({
  open,
  tab,
  onClose,
}: CreateAccountSheetProps) => {
  const f = useCreateAccountForm({ tab, onClose });

  // Reset whenever the sheet transitions to closed.
  useEffect(() => {
    if (!open)
      f.reset(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open]);

  if (!open) return null;

  const title = f.isCompany ? 'New Company' : 'New Internal Project';
  const emailErr =
    f.email.value && !f.email.valid
      ? f.email.errorMessage || 'Invalid email'
      : '';

  return (
    <div className="tw-fixed tw-inset-0 tw-z-40">
      <div
        onClick={onClose}
        className="tw-absolute tw-inset-0 tw-bg-[color:var(--backdrop)] tw-animate-fade-in"
      />
      <div
        className="tw-absolute tw-left-0 tw-right-0 tw-bottom-0 tw-bg-white tw-rounded-t-ds-xl tw-shadow-ds-e4 tw-flex tw-flex-col tw-animate-sheet-up"
        style={{ height: '92vh' }}
      >
        <div className="tw-flex tw-justify-center tw-pt-[10px] tw-pb-[2px] tw-flex-none">
          <span className="tw-w-[40px] tw-h-[4px] tw-rounded-full tw-bg-ds-hairline" />
        </div>

        {/* header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-[20px] tw-py-[12px] tw-border-b tw-border-ds-hairline tw-flex-none">
          {f.confirming ? (
            <button
              type="button"
              aria-label="Back"
              onClick={() => f.setConfirming(false)}
              className="tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-0 tw-flex"
            >
              <Icon name="arrow-left" size={22} color="var(--ink-soft)" />
            </button>
          ) : (
            <span className="tw-w-[22px]" />
          )}
          <h2 className="tw-font-poppins tw-text-[17px] tw-font-semibold tw-text-ds-ink tw-m-0">
            {f.confirming ? 'Confirm Account Creation' : title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-0 tw-flex"
          >
            <Icon name="x" size={22} color="var(--ink-soft)" />
          </button>
        </div>

        {/* body */}
        <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-px-[20px] tw-py-[18px]">
          {f.confirming ? (
            <div className="tw-font-poppins tw-text-[14px] tw-text-ds-storm tw-flex tw-flex-col tw-gap-[12px]">
              <p className="tw-m-0">
                You are about to create an account for <strong>{f.name}</strong>{' '}
                with the account email below:
              </p>
              <p className="tw-m-0 tw-font-semibold tw-text-ds-ink tw-break-all">
                {f.fullEmail}
              </p>
              <p className="tw-m-0 tw-text-ds-ink-soft">
                Please ensure this is correct. Account emails can no longer be
                edited upon confirmation.
              </p>
            </div>
          ) : (
            <div className="tw-flex tw-flex-col tw-gap-[18px]">
              {!f.isCompany && (
                <LabeledField label="Type" required>
                  <div className="tw-flex tw-gap-[8px]">
                    {TYPE_OPTIONS.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => f.setTypeId(t.value)}
                        className={clsx(
                          'tw-flex-1 tw-h-[40px] tw-rounded-full tw-border tw-cursor-pointer tw-font-poppins tw-text-[14px]',
                          f.typeId === t.value
                            ? 'tw-bg-ds-primary tw-text-white tw-border-ds-primary tw-font-semibold'
                            : 'tw-bg-white tw-text-ds-ink-soft tw-border-ds-hairline'
                        )}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </LabeledField>
              )}

              <LabeledField label="Name" required>
                <input
                  value={f.name}
                  onChange={(e) => f.onNameChange(e.target.value)}
                  placeholder={f.isCompany ? 'Company name' : 'Project name'}
                  className="tw-h-[40px] tw-w-full tw-box-border tw-px-[16px] tw-rounded-full tw-border tw-border-ds-hairline !tw-bg-white tw-font-poppins tw-text-[14px] tw-text-ds-storm tw-outline-none focus:tw-border-ds-primary"
                />
              </LabeledField>

              <LabeledField label="Account Lead" required>
                <LeadSelect
                  value={f.leadOption}
                  options={f.leadOptions}
                  loading={f.leadsLoading}
                  disabled={f.typeId === 3}
                  onOpen={f.openLeads}
                  onSelect={f.setLeadOption}
                />
              </LabeledField>

              <LabeledField label="Color Indicator" required>
                <ColorSwatchGrid value={f.color} onSelect={f.setColor} />
              </LabeledField>

              <LabeledField label="Account Email" required error={emailErr}>
                <div className="tw-flex tw-items-center tw-h-[40px] tw-rounded-full tw-border tw-border-ds-hairline !tw-bg-white tw-px-[16px] focus-within:tw-border-ds-primary">
                  <input
                    name="email"
                    value={f.email.value}
                    onChange={f.email.onChange}
                    onBlur={f.email.onValidate}
                    placeholder="account-email"
                    className="tw-flex-1 tw-min-w-0 tw-border-0 tw-outline-none !tw-bg-transparent tw-font-poppins tw-text-[14px] tw-text-ds-storm"
                  />
                  <span className="tw-font-poppins tw-text-[14px] tw-text-ds-ink-mute tw-flex-none">
                    @{f.emailSuffix}
                  </span>
                </div>
              </LabeledField>
            </div>
          )}
        </div>

        {/* footer */}
        <div
          className="tw-flex tw-gap-[10px] tw-px-[20px] tw-py-[14px] tw-border-t tw-border-ds-hairline tw-flex-none"
          style={{
            paddingBottom: 'calc(14px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          {f.confirming ? (
            <>
              <Button
                variant="outline"
                size="md"
                onClick={() => f.setConfirming(false)}
              >
                Back
              </Button>
              <span className="tw-flex-1">
                <Button variant="primary" size="md" onClick={f.submit}>
                  Confirm
                </Button>
              </span>
            </>
          ) : (
            <>
              <Button variant="outline" size="md" onClick={onClose}>
                Cancel
              </Button>
              <span className="tw-flex-1">
                <Button
                  variant="primary"
                  size="md"
                  disabled={!f.canSubmit}
                  onClick={() => f.setConfirming(true)}
                >
                  Create Account
                </Button>
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
